// Tiny DOM helpers shared by the views. Security rule they exist to enforce: never
// string-concatenate data into HTML. `fragment(markup)` parses a TRUSTED, STATIC HTML
// string (chrome you fully control, no interpolated data); `el(tag, attrs, ...children)`
// builds programmatically, routing every value through setAttribute / textContent so
// data is escaped by the DOM and never parsed as HTML. Live or untrusted data goes
// through `el`.

// markup must be trusted, static HTML (no interpolated data); use el() for data.
export function fragment(markup) {
  const template = document.createElement("template");
  template.innerHTML = markup;
  return template.content;
}

// The JSX pragma's fragment factory: esbuild compiles `<>...</>` to
// `el(Fragment, null, ...children)`; el sees this sentinel and builds a
// DocumentFragment instead of an element. A plain string constant (not an element name)
// so it never reaches createElement.
export const Fragment = "fragment-sentinel";

// Shared by elements and fragments so both honour the same "data is never HTML" rule:
// nullish/false children are skipped (so conditionals read cleanly) and strings/numbers
// become escaped text nodes.
function appendChildren(parent, children) {
  for (const child of children) {
    if (child === null || child === undefined || child === false) {
      continue;
    }
    parent.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
}

// Also the JSX factory: `<div class="x">hi</div>` compiles to
// `el("div", { class: "x" }, "hi")`. Because every value still flows through
// setAttribute / text nodes, JSX gets the SAME escaping guarantee as hand-written el()
// (the point of the demo: JSX is just sugar). attrs may be null; a `true` value sets an
// empty (boolean) attribute, false/null/undefined skip it.
export function el(tag, attrs = {}, ...children) {
  // Fragment is a sentinel STRING, not a tag name; compare as a string so the generic
  // `tag` type (element names) doesn't make this a "no-overlap" error.
  if ( (tag) === Fragment) {
    const frag = document.createDocumentFragment();
    appendChildren(frag, children);
    return  ( (frag));
  }
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs ?? {})) {
    if (value === null || value === undefined || value === false) {
      continue;
    }
    node.setAttribute(key, value === true ? "" : String(value));
  }
  appendChildren(node, children);
  return node;
}
